<template>
    <div class="rounded-lg shadow-card">
        <ScrollingTabsContainer
            ref="scrollingTabsContainer"
            :collapse-enabled="true"
            tabs-container-class="mx-4 xs:mx-6 md:mx-8"
            slot-class="p-4 xs:p-6 md:p-8"
        >
            <TabsItem :key="'description'" title="Description">
                <ProductViewDescriptionTab
                    :product="product"
                    :variation="selectedVariation"
                />
            </TabsItem>

            <TabsItem
                :key="'variationDetails'"
                :enabled="hasDetails"
                title="Product Details"
            >
                <ProductViewDetailsTab
                    v-if="selectedVariation"
                    :variation="selectedVariation"
                />
                <p v-else class="mb-4">
                    Please select all product specifications to see this
                    information.
                </p>
            </TabsItem>

            <TabsItem :key="'pricingTiers'" title="Price Breaks">
                <p v-if="priceBreakText" class="mb-4">
                    {{ priceBreakText }}
                </p>
                <ProductViewPricingTiersTab
                    v-if="pricingTiers.length"
                    :pricing-tiers="pricingTiers"
                />
                <p v-else class="mb-4">
                    Please select all product specifications to see this
                    information.
                </p>
            </TabsItem>
        </ScrollingTabsContainer>
    </div>
</template>

<script setup lang="ts">
import { Product, ProductData, ProductVariation } from '~/types/product';
import ProductViewDetailsTab from './additional-details-tabs/ProductViewDetailsTab.vue';
import ProductViewPricingTiersTab from './additional-details-tabs/ProductViewPricingTiersTab.vue';
import ProductViewDescriptionTab from './additional-details-tabs/ProductViewDescriptionTab.vue';
import ScrollingTabsContainer from '~/components/tabs/ScrollingTabsContainer.vue';
import { PricingTier } from '~/types/miscellaneous';

const props = defineProps<{
    product: Product;
    selectedProductData: ProductData | null;
    selectedVariation: ProductVariation | null;
    pricingTiers: PricingTier[];
}>();

const scrollingTabsContainer = ref<InstanceType<
    typeof ScrollingTabsContainer
> | null>(null);

const hasDetails = computed(() => {
    return props.product.variations.some(
        (item) => item.details && item.details.length > 0,
    );
});

const priceBreakText = computed(() => {
    if (
        !props.selectedProductData?.isCustomPrinted ||
        props.selectedProductData?.productTypeShortLabel === 'sticker' ||
        props.selectedProductData?.productTypeShortLabel === 'label'
    ) {
        return null;
    }

    if (props.selectedProductData?.productTypeShortLabel === 'bag') {
        return "Base product pricing doesn't include the cost for the additional features and accessories (e.g. degassing valve, tin tie, etc.)";
    }
    return "Base product pricing doesn't include the cost for the additional features and accessories.";
});

watch(
    [
        () => props.selectedVariation,
        () => props.selectedVariation,
        () => props.pricingTiers,
    ],
    () => {
        scrollingTabsContainer.value?.recalculateContentHeight();
    },
);
</script>
